import React, { useMemo } from 'react';
import { PROFILES } from '../profiles/index';
import { getFixHint } from '../lib/fix-hints';

export default function ValidationView({ profile, validation, onProceed, onStartOver, onBack }) {
  const profileDef = PROFILES[profile];
  const { errors, warnings, rows, summary } = validation;

  const errorCount = errors.length;
  const warningCount = warnings.length;
  const hasBlockingErrors = errorCount > 0;

  // Build a lookup of row → issues for highlighting
  const rowIssues = useMemo(() => {
    const map = {};
    [...errors, ...warnings].forEach((issue) => {
      const key = issue.row ?? 'global';
      if (!map[key]) map[key] = [];
      map[key].push(issue);
    });
    return map;
  }, [errors, warnings]);

  return (
    <div className="space-y-6">
      {/* Summary banner */}
      <div
        className={`rounded-lg p-5 border ${
          hasBlockingErrors
            ? 'bg-red-50 border-red-200'
            : warningCount > 0
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-green-50 border-green-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">
              {hasBlockingErrors
                ? `${errorCount} error${errorCount > 1 ? 's' : ''} found`
                : warningCount > 0
                ? `${warningCount} warning${warningCount > 1 ? 's' : ''}`
                : 'Validation passed'}
            </h2>
            <p className="text-sm mt-1 opacity-75">
              {profileDef.name} — {summary.totalRows} rows processed, {summary.totalAccounts} accounts
              {summary.droppedZeroLines > 0 &&
                `, ${summary.droppedZeroLines} $0 line${summary.droppedZeroLines > 1 ? 's' : ''} removed`}
            </p>
          </div>
          <div className="flex gap-3 text-sm">
            <div className="text-center px-3">
              <div className="text-2xl font-bold text-red-600">{errorCount}</div>
              <div className="text-xs text-gray-500">Errors</div>
            </div>
            <div className="text-center px-3">
              <div className="text-2xl font-bold text-yellow-600">{warningCount}</div>
              <div className="text-xs text-gray-500">Warnings</div>
            </div>
          </div>
        </div>
      </div>

      {/* Issues list */}
      {(errorCount > 0 || warningCount > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-medium text-sm">Issues</h3>
          </div>
          <div className="max-h-60 overflow-y-auto divide-y divide-gray-100">
            {errors.map((issue, i) => {
              const hint = getFixHint(issue);
              return (
                <div key={`e-${i}`} className="px-4 py-2 flex items-start gap-3 text-sm">
                  <span className="shrink-0 mt-0.5 w-5 h-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-bold">
                    ✕
                  </span>
                  <div>
                    <span className="font-medium">{issue.field}</span>
                    {issue.row != null && (
                      <span className="text-gray-400 ml-2">Row {issue.row + 1}</span>
                    )}
                    <p className="text-gray-500">{issue.message}</p>
                    {hint && (
                      <p className="text-xs text-mv-600 mt-1 bg-mv-50 px-2 py-1 rounded inline-block">
                        💡 {hint}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
            {warnings.map((issue, i) => {
              const hint = getFixHint(issue);
              return (
                <div key={`w-${i}`} className="px-4 py-2 flex items-start gap-3 text-sm">
                  <span className="shrink-0 mt-0.5 w-5 h-5 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center text-xs font-bold">
                    !
                  </span>
                  <div>
                    <span className="font-medium">{issue.field}</span>
                    {issue.row != null && (
                      <span className="text-gray-400 ml-2">Row {issue.row + 1}</span>
                    )}
                    <p className="text-gray-500">{issue.message}</p>
                    {hint && (
                      <p className="text-xs text-mv-600 mt-1 bg-mv-50 px-2 py-1 rounded inline-block">
                        💡 {hint}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Data preview table */}
      {rows && rows.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-sm">Transformed Data Preview</h3>
            <span className="text-xs text-gray-400">{rows.length} rows</span>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 w-10">#</th>
                  {Object.keys(rows[0]).map((col) => (
                    <th key={col} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.slice(0, 100).map((row, i) => {
                  const issues = rowIssues[i];
                  const hasError = issues?.some((iss) => iss.severity === 'error');
                  const hasWarning = issues?.some((iss) => iss.severity === 'warning');
                  return (
                    <tr
                      key={i}
                      className={
                        hasError
                          ? 'bg-red-50'
                          : hasWarning
                          ? 'bg-yellow-50'
                          : i % 2 === 0
                          ? 'bg-white'
                          : 'bg-gray-50/50'
                      }
                    >
                      <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="px-3 py-1.5 whitespace-nowrap font-mono">
                          {val ?? ''}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > 100 && (
            <div className="px-4 py-2 bg-gray-50 text-xs text-gray-400 text-center border-t">
              Showing first 100 of {rows.length} rows
            </div>
          )}
        </div>
      )}

      {/* Fix guidance when errors exist */}
      {hasBlockingErrors && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-semibold text-sm mb-2">How to fix and re-try</h3>
          <ol className="text-sm text-gray-600 space-y-1.5 list-decimal list-inside">
            <li>Open your source Excel file and correct the issues listed above</li>
            <li>Save the file and go back to Prepare to re-upload</li>
            <li>Re-run validation — export is blocked until all errors are resolved</li>
          </ol>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={onBack ?? onStartOver}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
        >
          ← {hasBlockingErrors ? 'Back to Prepare' : 'Start Over'}
        </button>
        <button
          onClick={onProceed}
          disabled={hasBlockingErrors}
          className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
            hasBlockingErrors
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-mv-600 text-white hover:bg-mv-700 shadow-md'
          }`}
        >
          {hasBlockingErrors ? 'Fix Errors to Continue' : 'Download Transformed File →'}
        </button>
      </div>
    </div>
  );
}
